// src/pages/CanvasDashboard.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

import { 
    Box, 
    Typography, 
    Grid, 
    Paper, 
    CircularProgress, 
    Container, 
    Button,
    Modal,
    TextField,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Snackbar,
    Alert
} from '@mui/material';
import { 
    Add as AddIcon, 
    ArrowBack as ArrowBackIcon, 
    Edit as EditIcon, 
    Delete as DeleteIcon,
    Share as ShareIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

// --- MODAL DE CRIAÇÃO/EDIÇÃO DO NOME DO CANVAS ---
const CanvasModal = ({ isOpen, onClose, onSave, initialData }) => {
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  const isEditing = !!initialData;

  useEffect(() => {
    if (isOpen) {
      setName(isEditing ? initialData.name : '');
    }
  }, [isOpen, initialData, isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Por favor, insira um título.');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ title: name }); 
      onClose();
    } catch (error) {
      console.error('Erro ao salvar o canvas:', error);
      alert('Não foi possível salvar o modelo de negócio.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
        <Paper 
            sx={{ 
                position: 'absolute', top: '50%', left: '50%', 
                transform: 'translate(-50%, -50%)', width: 400,
                bgcolor: 'background.paper', boxShadow: 24, p: 4,
            }}
            component="form" 
            onSubmit={handleSubmit}
        >
            <Typography variant="h6" component="h2">
                {isEditing ? 'Editar Modelo de Negócio' : 'Criar Novo Modelo de Negócio'}
            </Typography>
            <TextField
                margin="normal" required fullWidth id="name"
                label="Título do Canvas" name="name" autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onClose} sx={{ mr: 1 }}>Cancelar</Button>
                <Button type="submit" variant="contained" disabled={isSaving}>
                    {isSaving ? 'Salvando...' : 'Salvar'}
                </Button>
            </Box>
        </Paper>
    </Modal>
  );
};

// --- MODAL DE COMPARTILHAMENTO (NOVO) ---
const ShareModal = ({ isOpen, onClose, canvas, onSave, onShowSnackbar }) => {
  const [shareAccess, setShareAccess] = useState('none');
  const [expirationType, setExpirationType] = useState('24h');
  const [customHours, setCustomHours] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && canvas) {
      setShareAccess(canvas.shareAccess || 'none');
      
      // Determinar o tipo de expiração baseado nos dados existentes
      if (canvas.shareExpiresAt === null || canvas.shareExpiresAt === undefined) {
        setExpirationType('never');
      } else if (canvas.shareExpiresAt) {
        const expiresAt = new Date(canvas.shareExpiresAt);
        const now = new Date();
        const diffHours = (expiresAt - now) / (1000 * 60 * 60);
        
        if (diffHours <= 24) setExpirationType('24h');
        else if (diffHours <= 168) setExpirationType('7d');
        else if (diffHours <= 720) setExpirationType('30d');
        else {
          setExpirationType('custom');
          setCustomHours(Math.round(diffHours).toString());
        }
      }
    }
  }, [isOpen, canvas]);

  const saveShareSettings = async (access, expType, hours = null) => {
    setIsSaving(true);
    
    try {
      let expiresInHours = null;
      
      switch (expType) {
        case '24h':
          expiresInHours = 24;
          break;
        case '7d':
          expiresInHours = 168;
          break;
        case '30d':
          expiresInHours = 720;
          break;
        case 'custom':
          expiresInHours = hours || parseInt(customHours);
          break;
        case 'never':
          expiresInHours = -1; // Indica acesso ilimitado
          break;
        default:
          expiresInHours = 24;
      }
      
      const response = await api.put(`/models/${canvas.id}/share`, { 
        shareAccess: access,
        expiresInHours: expiresInHours
      });
      
      onSave(response.data);
      
    } catch (error) {
      console.error('Erro ao atualizar compartilhamento:', error);
      onShowSnackbar('Não foi possível atualizar o link.', 'error');
      // Reverter mudanças em caso de erro
      setShareAccess(canvas.shareAccess || 'none');
    } finally {
      setIsSaving(false);
    }
  };

  const handleShareAccessChange = async (event) => {
    const newAccess = event.target.value;
    setShareAccess(newAccess);
    
    if (newAccess === 'none') {
      // Desativar compartilhamento
      await saveShareSettings(newAccess, expirationType);
    }
  };

  const handleExpirationChange = async (event) => {
    const newExpiration = event.target.value;
    setExpirationType(newExpiration);
    
    if (shareAccess !== 'none' && newExpiration !== 'custom') {
      await saveShareSettings(shareAccess, newExpiration);
    }
  };

  const handleCustomHoursChange = (event) => {
    const value = event.target.value;
    // Permitir apenas números
    if (value === '' || /^\d+$/.test(value)) {
      setCustomHours(value);
    }
  };

  const handleCustomHoursBlur = async () => {
    if (expirationType === 'custom' && customHours && shareAccess !== 'none') {
      const hours = parseInt(customHours);
      if (hours > 0) {
        await saveShareSettings(shareAccess, 'custom', hours);
      }
    }
  };

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/shared/canvas/${canvas.shareToken}`;
    
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
        onShowSnackbar('Link copiado com sucesso!', 'success');
        return;
      }
    } catch (err) {
      console.warn("Clipboard API bloqueada, tentando fallback...");
    }

    try {
      const inputElement = document.getElementById("share-link-input");
      if (inputElement) {
        inputElement.select();
        inputElement.setSelectionRange(0, 99999);
        
        const successful = document.execCommand('copy');
        if (successful) {
          onShowSnackbar('Link copiado com sucesso!', 'success');
          return;
        }
      }
    } catch (fallbackErr) {
      console.warn("execCommand bloqueado.");
    }

    const inputElement = document.getElementById("share-link-input");
    if (inputElement) {
      inputElement.focus();
      inputElement.select();
    }
    
    onShowSnackbar('Por favor, pressione Ctrl+C (ou Cmd+C) para copiar o link.', 'info');
  };

  const formatExpirationDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Paper 
        sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          width: 500,
          maxWidth: '90vw',
          bgcolor: 'background.paper', 
          boxShadow: 24, 
          p: 4,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
          Compartilhar: {canvas?.name}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure as permissões e o tempo de expiração do link de compartilhamento.
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="share-access-label">Permissão de Acesso</InputLabel>
          <Select
            labelId="share-access-label"
            value={shareAccess}
            label="Permissão de Acesso"
            onChange={handleShareAccessChange}
            disabled={isSaving}
          >
            <MenuItem value="none">Desativado (Apenas eu tenho acesso)</MenuItem>
            <MenuItem value="view">Visualização (Apenas leitura)</MenuItem>
            <MenuItem value="edit">Edição (Pode alterar o Canvas)</MenuItem>
          </Select>
        </FormControl>

        {shareAccess !== 'none' && (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="expiration-label">Expiração do Link</InputLabel>
              <Select
                labelId="expiration-label"
                value={expirationType}
                label="Expiração do Link"
                onChange={handleExpirationChange}
                disabled={isSaving}
              >
                <MenuItem value="24h">24 Horas</MenuItem>
                <MenuItem value="7d">7 Dias</MenuItem>
                <MenuItem value="30d">30 Dias</MenuItem>
                <MenuItem value="custom">Personalizado</MenuItem>
                <MenuItem value="never">Ilimitado (Sem expiração)</MenuItem>
              </Select>
            </FormControl>

            {expirationType === 'custom' && (
              <TextField
                fullWidth
                label="Horas até expirar"
                type="number"
                value={customHours}
                onChange={handleCustomHoursChange}
                onBlur={handleCustomHoursBlur}
                disabled={isSaving}
                sx={{ mb: 3 }}
                InputProps={{
                  inputProps: { min: 1 },
                  endAdornment: <InputAdornment position="end">horas</InputAdornment>,
                }}
                helperText="Digite o número de horas que o link ficará ativo"
              />
            )}

            {canvas?.shareToken && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  id="share-link-input"
                  fullWidth
                  label="Link de Compartilhamento"
                  value={`${window.location.origin}/shared/canvas/${canvas.shareToken}`}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={handleCopyLink} 
                          title="Copiar Link" 
                          color="primary"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                
                {canvas.shareExpiresAt && expirationType !== 'never' && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#fff3e0', 
                    borderRadius: 1,
                    border: '1px solid #ffb74d'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#e65100', 
                        display: 'flex', 
                        alignItems: 'center', 
                        fontWeight: 500 
                      }}
                    >
                      ⏳ Este link expira em: {formatExpirationDate(canvas.shareExpiresAt)}
                    </Typography>
                  </Box>
                )}
                
                {expirationType === 'never' && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#e8f5e9', 
                    borderRadius: 1,
                    border: '1px solid #81c784'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#2e7d32', 
                        display: 'flex', 
                        alignItems: 'center', 
                        fontWeight: 500 
                      }}
                    >
                      🔓 Este link não tem data de expiração (acesso ilimitado)
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" onClick={onClose}>
            Concluído
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

// Componente Principal do Dashboard
const CanvasDashboard = () => {
  const [canvases, setCanvases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados do Modal de Criação/Edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCanvas, setEditingCanvas] = useState(null);

  // Estados do Modal de Compartilhamento
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharingCanvas, setSharingCanvas] = useState(null);

  // Estado do Snackbar
  const [snackbarInfo, setSnackbarInfo] = useState({ open: false, message: '', severity: 'success' });

  const isEmbedded = window.self !== window.top;

  const fetchCanvases = async () => {
    try {
      setLoading(true);
      const response = await api.get('/models');
      setCanvases(response.data);
    } catch (err) {
      setError('Não foi possível carregar os modelos de negócio.');
      console.error("Erro ao buscar dados da API:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCanvases();
  }, []);

  const handleOpenCreateModal = () => {
    setEditingCanvas(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (canvas) => {
    setEditingCanvas(canvas);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCanvas(null);
  };

  const handleSaveCanvas = async (canvasData) => {
    if (editingCanvas) {
      const response = await api.put(`/models/${editingCanvas.id}`, canvasData);
      setCanvases(canvases.map(c => c.id === editingCanvas.id ? response.data : c));
    } else {
      const response = await api.post('/models', canvasData);
      setCanvases(prevCanvases => [response.data, ...prevCanvases]);
    }
  };

  const handleDeleteCanvas = async (canvasId) => {
    if (window.confirm('Tem certeza que deseja apagar este modelo de negócio?')) {
      try {
        await api.delete(`/models/${canvasId}`);
        setCanvases(canvases.filter(c => c.id !== canvasId));
      } catch (error) {
        console.error('Erro ao apagar o canvas:', error);
        alert('Não foi possível apagar o modelo de negócio.');
      }
    }
  };

  const handleOpenShareModal = (canvas) => {
    setSharingCanvas(canvas);
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setSharingCanvas(null);
  };

  const handleShareUpdate = (updatedData) => {
    setCanvases(canvases.map(c => 
      c.id === sharingCanvas.id 
        ? { ...c, ...updatedData } 
        : c
    ));
    setSharingCanvas(prev => ({ ...prev, ...updatedData }));
  };

  const handleShowSnackbar = (message, severity = 'success') => {
    setSnackbarInfo({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarInfo(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }
  if (error) {
    return <Typography color="error" sx={{ p: 4 }}>Erro: {error}</Typography>;
  }

  return (
    <>
      <Container maxWidth="xl" sx={{ py: isEmbedded ? 2 : 0 }}>
        {!isEmbedded && (
            <Button component={Link} to="/" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                Voltar para a Página Inicial
            </Button>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, borderBottom: '1px solid #eee', pb: 3 }}>
            <div>
                <Typography variant="h4" component="h1" sx={{ color: '#333', fontWeight: 'bold', fontSize: isEmbedded ? '1.8rem' : '2.125rem' }}>
                    Desenvolvimento do Modelo de Negócio
                </Typography>
                <Typography sx={{ color: '#666', mt: 1, fontSize: isEmbedded ? '0.9rem' : '1rem' }}>
                    Dashboard com todos os seus Business Model Canvas.
                </Typography>
            </div>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateModal}>
                Criar Novo Canvas
            </Button>
        </Box>

        <Box>
            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
                Meus Modelos
            </Typography>
            {canvases.length === 0 ? (
                <Typography>
                    Nenhum modelo de negócio encontrado. Clique no botão acima para criar o primeiro!
                </Typography>
            ) : (
                <Grid container spacing={3}>
                    {canvases.map((canvas) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={canvas.id} sx={{ display: 'flex' }}>
                            <Paper 
                                elevation={2}
                                sx={{ 
                                    p: 2.5, width: '100%', display: 'flex', flexDirection: 'column',
                                    transition: 'transform 0.3s, box-shadow 0.3s',
                                    '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                                }}
                            >
                                <Box component={Link} to={`/canvas/${canvas.id}`} sx={{ textDecoration: 'none', color: 'inherit', flexGrow: 1 }}>
                                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.1rem' }}>
                                        {canvas.name}
                                        {canvas.shareAccess !== 'none' && (
                                            <Typography component="span" sx={{ ml: 1, fontSize: '0.7rem', bgcolor: '#e3f2fd', color: '#1976d2', px: 1, py: 0.2, borderRadius: 1 }}>
                                                Compartilhado
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Atualizado em: {new Date(canvas.updatedAt).toLocaleDateString()}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, mt: 1, borderTop: '1px solid #f0f0f0' }}>
                                    <IconButton size="small" onClick={() => handleOpenShareModal(canvas)} title="Compartilhar">
                                        <ShareIcon fontSize="small" color={canvas.shareAccess !== 'none' ? 'primary' : 'inherit'} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleOpenEditModal(canvas)} title="Editar Título">
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteCanvas(canvas.id)} title="Apagar">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
      </Container>
      
      <CanvasModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveCanvas}
        initialData={editingCanvas}
      />

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        canvas={sharingCanvas}
        onSave={handleShareUpdate}
        onShowSnackbar={handleShowSnackbar}
      />

      <Snackbar 
        open={snackbarInfo.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbarInfo.severity} 
            sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbarInfo.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CanvasDashboard;